import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from 'reactstrap';

import '../../../assets/scss/datatables.scss';
import { EXPORT_ALLOWED_USER_ROLES } from '../../../constants';
import { MetaTags } from 'react-meta-tags';
import { DashBreadcrumb } from '../../../components/Common/Breadcrumb';
import ItemList from './ItemList';
import AddServiceItemOffcanvas from './AddServiceItemOffcanvas';
import {
  createServiceItem,
  updateServiceItem,
  disableServiceItem,
} from './actions/action';
import { useDispatch } from 'react-redux';
import toast, { Toaster } from 'react-hot-toast';

const ServiceItemMaster = () => {
  const dispatch = useDispatch();
  const [isOffcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false, id: null, status: null });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isDisabling, setIsDisabling] = useState(false);

  const toggleOffcanvas = () => {
    if (isOffcanvasOpen) {
      setEditData(null);
    }
    setOffcanvasOpen(!isOffcanvasOpen);
  };

  const handleEditItem = (item) => {
    setEditData(item);
    setOffcanvasOpen(true);
  };

  const handleDisableItem = (id, newStatus) => {
    setConfirmModal({ show: true, id, status: newStatus });
  };

  const confirmDisable = async () => {
    const { id, status } = confirmModal;
    setIsDisabling(true);
    try {
      const response = await disableServiceItem({ id, status, dispatch });
      if (response?.data?.code === 100) {
        toast.success(`Service Item ${status === 1 ? 'enabled' : 'disabled'} successfully!`);
        setConfirmModal({ show: false, id: null, status: null });
        setRefreshTrigger((prev) => prev + 1);
        setIsDisabling(false);
        return true;
      } else {
        toast.error(
          response?.data?.message || 'Failed to update service item status',
        );
        setIsDisabling(false);
        return false;
      }
    } catch (error) {
      toast.error('Something went wrong!');
      setIsDisabling(false);
      return false;
    }
  };

  const handleSaveItem = async (data) => {
    try {
      let response;
      if (editData) {
        response = await updateServiceItem({ id: data.id, data, dispatch });
      } else {
        response = await createServiceItem({ data, dispatch });
      }

      if (response?.data?.code === 100) {
        toast.success(
          editData
            ? 'Service Item updated successfully!'
            : 'Service Item added successfully!',
        );
        setOffcanvasOpen(false);
        setEditData(null);
        setRefreshTrigger((prev) => prev + 1);
        return true;
      } else {
        toast.error(response?.data?.message || 'Failed to save service item');
        return false;
      }
    } catch (error) {
      toast.error('Something went wrong!');
      return false;
    }
  };

  return (
    <>
      <div className="page-content">
        <MetaTags>
          <title>Mange Service Items</title>
        </MetaTags>
        <Container fluid>
          {/* Header Section */}
          <Row>
            <Col xl={4}>
              <DashBreadcrumb
                title="Service Orders"
                breadcrumbItem="Manage Service Items"
              />
            </Col>
            <Col xl={8}>
              <div className="d-flex justify-content-end align-items-center mb-3">
                <div className="DashbordFliter me-2">
                  <button
                    type="button"
                    className="btn btn-dark"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={toggleOffcanvas}
                  >
                    <i className="fa fa-plus me-2"></i>
                    Add Service Item
                  </button>
                </div>
                {/* {EXPORT_ALLOWED_USER_ROLES.includes(String(users?.role_id)) && (
                  <div>
                    <ServiceOrderExportReport />
                  </div>
                )} */}
              </div>
            </Col>
          </Row>

          <ItemList
            onEdit={handleEditItem}
            onDisable={handleDisableItem}
            refreshTrigger={refreshTrigger}
          />
          <AddServiceItemOffcanvas
            isOpen={isOffcanvasOpen}
            toggle={toggleOffcanvas}
            onSave={handleSaveItem}
            editData={editData}
          />

          <Modal
            isOpen={confirmModal.show}
            toggle={() => setConfirmModal({ show: false, id: null, status: null })}
            centered
          >
            <ModalHeader
              toggle={() => setConfirmModal({ show: false, id: null, status: null })}
            >
              Confirm Action
            </ModalHeader>
            <ModalBody className="text-center py-4">
              <i className={`mdi mdi-alert-circle-outline display-4 mb-3 ${confirmModal.status === 1 ? 'text-info' : 'text-warning'}`}></i>
              <h5>Are you sure you want to {confirmModal.status === 1 ? 'enable' : 'disable'} this service item?</h5>
              <p className="text-muted">
                This action will {confirmModal.status === 1 ? 'activate' : 'deactivate'} the item in the system.
              </p>
            </ModalBody>
            <ModalFooter className="gap-2">
              <Button
                color="btn btn-light"
                outline
                onClick={() => setConfirmModal({ show: false, id: null, status: null })}
                disabled={isDisabling}
              >
                Cancel
              </Button>
              <Button color="btn btn-secondary" onClick={confirmDisable} disabled={isDisabling}>
                {isDisabling && <Spinner size="sm" className="me-2" />}
                Yes, {confirmModal.status === 1 ? 'Enable' : 'Disable'} it
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
      <Toaster position="top-right" reverseOrder={false} />
    </>
  );
};

export default ServiceItemMaster;
